
import { InvoiceCreationPeriod } from '@/functions/subscription-functions';

export enum InvoiceStatus {
  UnSent = 'un-sent', // File not uploaded and due date not set.
  Sent = 'sent', // File and due date are set.
  UnPaid = 'un-paid', // Overdue.
  Paid = 'paid',
  Canceled = 'canceled'
}

export enum OrderType {
  'Posting' = 'Posting',
  'Other' = 'Other',
  'Candidate' = 'Candidate',
}

export interface BaseOrder
{
	id: number;
	title: string;
	description: string;
	total: number;
	orderType: OrderType;
	currentDue: number;
	isPaymentCanceled: boolean;
	discount: number;
}

export const IsPostingOrder = (order: BaseOrder): order is PostingOrder => 
	order !== null && order !== undefined && order.orderType === OrderType.Posting;

export const IsCandidateOrder = (order: BaseOrder): order is CandidatePackagePostingOrder => 
	order !== null && order !== undefined && order.orderType === OrderType.Candidate;

export const IsOrder = (order: BaseOrder): order is Order => 
	order !== null && order !== undefined && order.orderType === OrderType.Other;

export const IsCandidateOrPostingOrder = (order: BaseOrder): order is PostingOrder | CandidatePackagePostingOrder => IsCandidateOrder(order) || IsPostingOrder(order);

export const IsCandidateOrPostingOrderArray = (orders: BaseOrder[]): orders is (PostingOrder | CandidatePackagePostingOrder)[] => orders.every(IsCandidateOrPostingOrder);

export const IsPostingOrderArray = (orders: BaseOrder[]): orders is PostingOrder[] => orders.every(IsPostingOrder);

export const IsCandidatePostingOrderArray = (orders: BaseOrder[]): orders is CandidatePackagePostingOrder[] => orders.every(IsCandidateOrder);

export const FilterPostingOrders = (orders: BaseOrder[]): PostingOrder[] => orders.filter(IsPostingOrder);

export const FilterCandidatePostingOrders = (orders: BaseOrder[]): CandidatePackagePostingOrder[] => orders.filter(IsCandidateOrder);

export interface Order extends BaseOrder
{
	orderType: OrderType.Other;
}

export interface PostingOrder extends BaseOrder {
  orderType: OrderType.Posting;
  starts: string;
  expires: string;
  days: number;
  pricePerDay: number;
  posting: OrderPosting;
}

export interface CandidatePackagePostingOrder extends BaseOrder {
  orderType: OrderType.Candidate;
  starts: string;
  posting: OrderPosting;
}

export interface OrderPosting {
  id: number;
  place?: string;
}

export interface CompanyOrders {
  id: number;
  name: string;
  billingName: string;
  billingProfileId: number;
  vatAmount: number;
  paperBilling: boolean;
  fortnoxCustomerNumber: string;
  status: InvoiceStatus.UnPaid;
  companyHasSubscription: boolean;
  lastInvoiceCreatedAt: string | null;
  invoiceCreationPeriod: InvoiceCreationPeriod;
  orders: BaseOrder[];
}

export interface AdminInvoice extends AdminInvoiceView {
	bill: OfficeFile;

	orders: BaseOrder[];
}

export interface AdminInvoiceView {
	id: number;

	companyId: number;

	companyName: string;

	companyOrgNr: string;

	billingName: string;

	fortnoxCustomerNumber: string;

	fortnoxInvoiceNumber: number | null;

	status: InvoiceStatus;

	orderNumber: string;

	amount: number;

	vat: number;

	fortnoxTotal: number | null;

	fortnoxVat: number | null;

	start?: string;

	dueDate?: string;

	ordersCount: number;

	paperBilling: boolean;

	createdAt: string;
}

export interface OfficeFile {
	id: number;

	name: string;

	publicId: string;

	bytes: ArrayBuffer;

	contentType: string;

	hash: string;
}